import React, { useState } from "react";
import GoldCoin from "../images/Gold_coin.png";
import SilverCoin from "../images/Silver_coin.png";
import CopperCoin from "../images/Copper_coin.png";
import { pickQuagganEmoji, getEmojiByName } from "../utils/quagganEmojiPool.js";

function ordinal(position) {
  if (position === 1) return "1st";
  if (position === 2) return "2nd";
  return "3rd";
}

function medal(position) {
  const medalByPosition = {
    1: { src: GoldCoin, alt: "Gold medal" },
    2: { src: SilverCoin, alt: "Silver medal" },
    3: { src: CopperCoin, alt: "Bronze medal" },
  };
  const selectedMedal = medalByPosition[position];
  if (!selectedMedal) return null;

  return (
    <img
      src={selectedMedal.src}
      alt={selectedMedal.alt}
      style={{
        width: 20,
        height: 20,
        objectFit: "contain",
        verticalAlign: "middle",
      }}
    />
  );
}

function formatScore(value) {
  return `${value >= 0 ? "+" : ""}${value.toFixed(2)}`;
}

function RankingEntry({ row, position, positive, accent }) {
  const [emojiSrc] = useState(() =>
    pickQuagganEmoji({ type: positive ? "success" : "failure" }),
  );

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "48px minmax(0, 1fr) auto",
        gap: 14,
        alignItems: "center",
        padding: "9px 10px",
        borderRadius: 11,
        background: "rgba(10, 28, 55, 0.78)",
        border: "1px solid rgba(144,224,239,0.12)",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          alignSelf: "stretch",
          minHeight: 76,
          gap: 6,
        }}
      >
        {emojiSrc ? (
          <img
            src={emojiSrc}
            alt={`${positive ? "Lucky" : "Unlucky"} Quaggan`}
            style={{ width: 42, height: 42, objectFit: "contain", flexShrink: 0 }}
          />
        ) : null}
        <strong
          style={{
            color: "#ffea9a",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            whiteSpace: "nowrap",
          }}
        >
          {ordinal(position)} {medal(position)}
        </strong>
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          title={row.name}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 0,
            color: "#eefbff",
            fontWeight: 700,
            overflow: "hidden",
            whiteSpace: "nowrap",
            marginBottom: 8,
          }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{row.name}</span>
        </div>

        <div style={{ color: "rgba(214,232,255,0.78)", fontSize: 12, lineHeight: 1.5 }}>
          Participated in {row.gamesParticipated} giveaways.
        </div>
        <div style={{ color: "rgba(214,232,255,0.78)", fontSize: 12, lineHeight: 1.5 }}>
          Actual wins: {row.actualWins}. Expected wins: about {row.expectedWins.toFixed(2)}.
        </div>
        <div style={{ color: "rgba(214,232,255,0.78)", fontSize: 12, lineHeight: 1.5 }}>
          {row.winDifference >= 0
            ? `${row.winDifference.toFixed(2)} more wins than expected.`
            : `${Math.abs(row.winDifference).toFixed(2)} fewer wins than expected.`}
        </div>
      </div>

      <strong style={{ color: accent, whiteSpace: "nowrap" }} title="Standardized luck score">
        Luck score: {formatScore(row.luckScore)}
      </strong>
    </div>
  );
}

function RankingCard({ title, icon, rows, positive, subtitle }) {
  const accent = positive ? "#9ef0b0" : "#ff9d9d";

  return (
    <section
      style={{
        borderRadius: 16,
        border: `1px solid ${positive ? "rgba(110, 220, 140, 0.3)" : "rgba(255, 120, 120, 0.3)"}`,
        background: "rgba(8, 24, 50, 0.72)",
        padding: 14,
        minWidth: 0,
      }}
    >
      <h4
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          margin: "0 0 10px",
          color: accent,
          fontSize: 16,
        }}
      >
        {icon} {title}
      </h4>
      {subtitle ? (
        <p
          className="login-help-text"
          style={{ margin: "-4px 0 10px", fontSize: 12 }}
        >
          {subtitle}
        </p>
      ) : null}
      {rows.length === 0 ? (
        <div
          style={{
            color: "rgba(224,247,250,0.78)",
            fontSize: 13,
            padding: "10px 4px",
          }}
        >
          No qualified participants are available for this ranking.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {rows.map((row, index) => (
            <RankingEntry
              key={row.key}
              row={row}
              position={index + 1}
              positive={positive}
              accent={accent}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export default function LuckStatistics({ luckiest, unluckiest, minimumGames }) {
  const hasRankings = luckiest.length > 0 || unluckiest.length > 0;

  return (
    <section style={{ marginTop: 16 }}>
      <div style={{ marginBottom: 10 }}>
        <h3 style={{ margin: 0, color: "#40e8ff" }}>Luck Statistics</h3>
        <p className="login-help-text" style={{ margin: "6px 0 0" }}>
          We compare each participant&apos;s actual wins with how many wins were
          statistically expected. A positive luck score means they won more
          often than expected; a negative score means they won less often.
        </p>
        <p className="login-help-text" style={{ margin: "6px 0 0" }}>
          A minimum of {minimumGames} games is required to qualify for luck
          rankings.
        </p>
      </div>
      {!hasRankings ? (
        <div
          style={{
            borderRadius: 16,
            border: "1px dashed rgba(144,224,239,0.35)",
            background: "rgba(8, 24, 50, 0.55)",
            padding: 18,
            textAlign: "center",
            color: "rgba(224,247,250,0.78)",
          }}
        >
          Participants need at least {minimumGames} games to qualify for luck
          rankings.
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 12,
          }}
        >
          <RankingCard
            title="Luckiest Quaggans"
            icon={
              <img
                src={getEmojiByName("quaggan_rich")}
                alt=""
                style={{
                  width: 28,
                  height: 28,
                  objectFit: "contain",
                  verticalAlign: "middle",
                }}
              />
            }
            rows={luckiest}
            positive
          />
          <RankingCard
            title="Unluckiest Quaggans"
            icon={
              <img
                src={getEmojiByName("QuagTableFlip")}
                alt=""
                style={{
                  width: 28,
                  height: 28,
                  objectFit: "contain",
                  verticalAlign: "middle",
                }}
              />
            }
            rows={unluckiest}
            positive={false}
          />
        </div>
      )}
    </section>
  );
}
